"use client"
import "keen-slider/keen-slider.min.css"
import { useState, useEffect, useCallback } from "react"
import Image from "next/image"
import CustomImage from "components/atoms/Image/CustomImage"
import { AdvertisementResponse } from "types/Advertisements"
import useWindowSize from "hooks/useWindowSize"
import { useRouter } from "next/router"
import { useLang } from "hooks/useLang"
import KeenSlider from "keen-slider"
import AppDownloadModal from "components/download_the_app/AppDownloadModal"

/* =======================
   CONSTANTS
======================= */

const AUTO_SCROLL_DELAY = 5000

const APP_STORE_URL = "https://apps.apple.com/us/app/pass-tickets/id1596912352"

const PLAY_STORE_URL =
    "https://play.google.com/store/apps/details?id=com.starthub.passtickets"

/* =======================
   DEVICE DETECTION
======================= */

const isIOS = () =>
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent)

const isAndroid = () =>
    typeof navigator !== "undefined" && /Android/.test(navigator.userAgent)

const isMobileDevice = () => isIOS() || isAndroid()

/* =======================
   TYPES
======================= */

interface PageProps {
    ads: AdvertisementResponse["payload"]
    sliderRef: any
    instanceRef: any
    currentSlide: number
    loaded: boolean
}

/* =======================
   COMPONENT
======================= */

const AdsSlider = ({
    ads,
    currentSlide,
    loaded,
    instanceRef,
    sliderRef,
}: PageProps) => {
    const [showAppModal, setShowAppModal] = useState(false)

    const advertisementSliders = ads?.filter(
        (ad) => ad.web_view_type === "slider"
    )

    const router = useRouter()
    const { isMobile } = useWindowSize()
    const { t, lang } = useLang()
    const isArabic = lang === "ar"

    /* =======================
     SLIDER DOT CLICK
  ======================= */

    const handleDotClick = useCallback(
        (index: number) => {
            instanceRef.current?.moveToIdx(index)
        },
        [instanceRef]
    )

    /* =======================
     SLIDER INIT
  ======================= */

    useEffect(() => {
        if (
            sliderRef.current &&
            !instanceRef.current &&
            advertisementSliders?.length
        ) {
            instanceRef.current = new KeenSlider(
                sliderRef.current,
                {
                    loop: true,
                    defaultAnimation: { duration: 500 },
                    drag: true,
                    rubberband: true,
                },
                [
                    (slider) => {
                        let timeout: any

                        const clearNextTimeout = () => clearTimeout(timeout)
                        const setupNextTimeout = () => {
                            clearTimeout(timeout)
                            timeout = setTimeout(
                                () => slider.next(),
                                AUTO_SCROLL_DELAY
                            )
                        }

                        slider.on("created", setupNextTimeout)
                        slider.on("dragStarted", clearNextTimeout)
                        slider.on("animationEnded", setupNextTimeout)
                        slider.on("updated", setupNextTimeout)
                    },
                ]
            )
        }

        return () => {
            if (instanceRef.current) {
                instanceRef.current.destroy()
                instanceRef.current = null
            }
        }
    }, [sliderRef, instanceRef, advertisementSliders?.length])

    if (!advertisementSliders?.length) return null

    /* =======================
     🔥 GET THE APP HANDLER
  ======================= */

    const handleGetTheApp = () => {
        // ✅ Mobile browser → redirect to store
        if (typeof window !== "undefined" && isMobileDevice()) {
            if (isIOS()) {
                window.location.href = APP_STORE_URL
            } else if (isAndroid()) {
                window.location.href = PLAY_STORE_URL
            }
            return
        }

        // ✅ Desktop / Laptop → open modal
        setShowAppModal(true)
    }

    return (
        <>
            <div
                className="layout md:max-w-[1220px] w-full lg:mt-[84px] mt-[100px] relative"
                dir={isArabic ? "rtl" : "ltr"}
            >
                {/* Animations */}
                <style>{`
          @keyframes slideInFade {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes scaleIn {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          .animate-scaleIn {
            animation: scaleIn 0.25s ease-out forwards;
          }

          @keyframes dotPulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); }
          }

          .slider-enter {
            animation: slideInFade 0.6s ease-out forwards;
          }

          .dot-active {
            animation: dotPulse 0.4s ease-out;
          }
        `}</style>

                {/* Slider */}
                <div
                    ref={sliderRef}
                    className="keen-slider lg:aspect-[16/9] aspect-[3/4] overflow-hidden rounded-xl w-full slider-enter"
                    style={{ zIndex: 20 }}
                >
                    {advertisementSliders.map((img, idx) => (
                        <div
                            key={img._id}
                            className={`keen-slider__slide relative w-full h-full ${
                                img.type === "single" ? "cursor-pointer" : ""
                            }`}
                            onClick={() => {
                                if (img.type === "single") {
                                    router.push(`/events/${img.events[0]}`)
                                }
                            }}
                        >
                            <CustomImage
                                src={`${process.env.NEXT_PUBLIC_Event_Img_URL}${
                                    isMobile ? img.image : img.web_image
                                }`}
                                alt="Advertisement"
                                fill
                                priority={idx === 0}
                                className="object-cover hover:scale-105 transition-transform duration-500"
                            />
                        </div>
                    ))}
                </div>

                {/* Dots */}
                {loaded && advertisementSliders.length > 1 && (
                    <div
                        className={`absolute bottom-[100px] ${
                            isArabic ? "right-[60px]" : "left-[60px]"
                        } flex gap-2 z-[20]`}
                    >
                        {advertisementSliders.map((_, idx) => (
                            <span
                                key={idx}
                                onClick={() => handleDotClick(idx)}
                                className={`h-3 w-3 rounded-full cursor-pointer transition-all ${
                                    currentSlide === idx
                                        ? "bg-[#ED1A5A] dot-active"
                                        : "bg-white/80 hover:scale-110"
                                }`}
                            />
                        ))}
                    </div>
                )}

                {/* Blue Section */}
                {loaded && (
                    <div
                        className={`w-full overflow-hidden bg-[#49B4FF] h-[100px] flex items-center justify-between rounded-b-xl rounded-t-xl mt-[-18px] relative`}
                    >
                        <div className="mt-[15px] px-4 flex items-center gap-3 z-[20] flex-1">
                            <Image
                                alt="PassTickets logo"
                                src="/svg/passtickets.svg"
                                width={50}
                                height={50}
                                className="w-[50px] h-[50px]"
                            />
                            <span className="text-4 font-bold uppercase">
                                {t("enjoy_better_exp")}
                            </span>
                        </div>

                        <div
                            onClick={handleGetTheApp}
                            className="px-4 flex items-center cursor-pointer justify-end h-full flex-1"
                        >
                            <div className="mt-[15px] px-4 py-1 z-[2] items-center justify-center rounded-full bg-[#FC267A] hover:opacity-80 transition-all">
                                <span className="uppercase">
                                    {t("get_the_app")}
                                </span>
                            </div>
                        </div>

                        {/* Background Logo */}
                        <div
                            className={`absolute z-[1] opacity-30 w-[350px] h-[350px] rotate-[-12.65deg] ${
                                isArabic ? "left-9" : "right-9"
                            } bottom-[-130px]`}
                        >
                            <Image
                                alt="PassTickets background logo"
                                src="/images/sublogo.png"
                                width={350}
                                height={350}
                                className="w-full h-full"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* App Download Modal */}
            {showAppModal && (
                <AppDownloadModal
                    open={showAppModal}
                    onClose={() => setShowAppModal(false)}
                />
            )}
        </>
    )
}

export default AdsSlider
